// Методы авторизации для partner-lids через SSO

import ToastUtils from '../utils/toast';

// Ключ для хранения токена в localStorage
const TOKEN_STORAGE_KEY = 'partner-lids-token';

// Ключ для хранения refresh токена в localStorage
const REFRESH_TOKEN_STORAGE_KEY = 'partner-lids-refresh-token';

// Ключ для хранения времени истечения access токена
const ACCESS_TOKEN_EXPIRES_AT_KEY = 'partner-lids-access-expires-at';

// Ключ для хранения ID пользователя в localStorage
const USER_ID_STORAGE_KEY = 'partner-lids-user-id';

// Базовый URL для SSO API
const SSO_API_BASE_URL = '/sso/v1';
const API_BASE_URL = '/panel/v1';

// Сохранение токена, refresh токена, времени истечения и ID пользователя в localStorage
const saveAuthDataToStorage = (token, refreshToken, accessExpiresIn, id) => {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, (Date.now() + accessExpiresIn * 1000).toString());
    localStorage.setItem(USER_ID_STORAGE_KEY, id);
    return true;
  } catch (error) {
    console.error('Ошибка при сохранении данных аутентификации в localStorage:', error);
    return false;
  }
};

// Получение токена из localStorage
export const getTokenFromStorage = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Ошибка при получении токена из localStorage:', error);
    return null;
  }
};

// Получение refresh токена из localStorage
export const getRefreshTokenFromStorage = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Ошибка при получении refresh токена из localStorage:', error);
    return null;
  }
};

// Проверка, истек ли access токен
const isAccessTokenExpired = () => {
  try {
    const expiresAt = localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    if (!expiresAt) return true;
    return Date.now() >= parseInt(expiresAt, 10);
  } catch (error) {
    console.error('Ошибка при проверке истечения токена:', error);
    return true;
  }
};

// Получение ID пользователя из localStorage
export const getUserIdFromStorage = () => {
  try {
    const userId = localStorage.getItem(USER_ID_STORAGE_KEY);
    return userId ? parseInt(userId, 10) : null;
  } catch (error) {
    console.error('Ошибка при получении ID пользователя из localStorage:', error);
    return null;
  }
};

// Получение пользователя с сервера
const getUser = async () => {
  try {
    const userId = getUserIdFromStorage();
    
    if (!userId) {
      return null;
    }
    
    // Получаем актуальные заголовки авторизации (с автоматическим обновлением токена)
    const headers = await getAuthHeaders();
    
    // Проверяем, авторизован ли пользователь
    if (!headers['Authorization']) {
      return null;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}/profile`, {
      method: 'GET',
      headers: headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Ошибка при получении пользователя:', error);
    return null;
  }
};

// Удаление данных аутентификации из localStorage
const removeAuthDataFromStorage = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    localStorage.removeItem(USER_ID_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Ошибка при удалении данных аутентификации из localStorage:', error);
    return false;
  }
};

// Установка токена в заголовки для последующих запросов
const setAuthHeader = (token) => {
  if (token) {
    // Устанавливаем токен для всех последующих запросов
    // Это может быть реализовано через настройку axios или fetch interceptor
    // В данном случае мы просто возвращаем заголовки, которые нужно использовать
    return {
      'Authorization': `Bearer ${token}`
    };
  }
  return {};
};

// Проверка пользователя (логин) через SSO
export const login = (login, password) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Данные для отправки на сервер авторизации
      const loginData = {
        login: login,
        password: password
      };

      // Выполняем POST запрос к SSO API
      const response = await fetch(`${SSO_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Извлекаем токены и время истечения из ответа
      const token = data.data?.access_token;
      const refreshToken = data.data?.refresh_token;
      const accessExpiresIn = data.data?.access_expires_in;
      
      if (!token || !refreshToken || !accessExpiresIn) {
        throw new Error('Не все необходимые данные получены от сервера авторизации');
      }
      
      const responseUser  = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!responseUser.ok) {
        throw new Error(`HTTP error! status: ${responseUser.status}`);
      }
      
      const dataUser = await responseUser.json();
      
      // Сохраняем токены, время истечения и ID пользователя в localStorage
      const saved = saveAuthDataToStorage(token, refreshToken, accessExpiresIn, dataUser.data?.id);
      if (!saved) {
        throw new Error('Не удалось сохранить данные аутентификации');
      }
      
      // Возвращаем объект с токеном и ID пользователя
      resolve({ token, id: dataUser.data?.id });
    } catch (error) {
      console.error('Ошибка при авторизации:', error);
      const errorMessage = error.message.includes('HTTP error') ? 'Ошибка сервера авторизации' : 'Неверный логин или пароль';
      ToastUtils.error(errorMessage, { style: { backgroundColor: '#FEF7F7', color: '#E30000B2' }, icon: <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.75 9.167C0.75 5.969 0.75 4.37 1.128 3.832C1.505 3.295 3.008 2.78 6.015 1.751L6.588 1.555C8.155 1.018 8.938 0.75 9.75 0.75C10.562 0.75 11.345 0.75 12.912 1.555L13.485 1.751C16.492 2.78 17.995 3.295 18.372 3.832C18.75 4.37 18.75 5.97 18.75 9.167V10.741C18.75 16.379 14.511 19.116 11.851 20.277C11.13 20.592 10.77 20.75 9.75 20.75C8.73 20.75 8.37 20.592 7.649 20.277C4.989 19.115 0.75 16.38 0.75 10.741V9.167Z" stroke="#E30000" stroke-opacity="0.7" stroke-width="1.5"/>
      <path d="M9.75 6.75V10.75" stroke="#E30000" stroke-opacity="0.7" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M9.75 14.75C10.3023 14.75 10.75 14.3023 10.75 13.75C10.75 13.1977 10.3023 12.75 9.75 12.75C9.19772 12.75 8.75 13.1977 8.75 13.75C8.75 14.3023 9.19772 14.75 9.75 14.75Z" fill="#E30000" fill-opacity="0.7"/>
      </svg> });
      reject(new Error(errorMessage));
    }
  });
};

// Проверка, авторизован ли пользователь
export const isAuthenticated = () => {
  return getTokenFromStorage() !== null && getUserIdFromStorage() !== null;
};

// Обновление access токена с помощью refresh токена
const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshTokenFromStorage();
    
    if (!refreshToken) {
      throw new Error('Refresh token отсутствует');
    }
    
    const response = await fetch(`${SSO_API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Извлекаем новые токены и время истечения
    const newAccessToken = data.data?.access_token;
    const accessExpiresIn = data.data?.access_expires_in;
    // Используем старый refresh токен, так как сервер его не возвращает
    const newRefreshToken = refreshToken;
    
    if (!newAccessToken || !newRefreshToken || !accessExpiresIn) {
      throw new Error('Не все необходимые данные получены при обновлении токена');
    }
    
    // Сохраняем новые токены и время истечения
    const saved = saveAuthDataToStorage(newAccessToken, newRefreshToken, accessExpiresIn, getUserIdFromStorage());
    
    if (!saved) {
      throw new Error('Не удалось сохранить обновленные данные аутентификации');
    }
    
    return newAccessToken;
  } catch (error) {
    console.error('Ошибка при обновлении access токена:', error);
    // Удаляем все данные аутентификации при ошибке обновления
    removeAuthDataFromStorage();
    throw error;
  }
};

// Получение авторизованного пользователя из сессии
export const getAuthenticatedUser = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const userData = await getUser();
      console.log('getUserData:', userData);
      if (userData) {
        // Проверяем, что у пользователя есть одна из разрешенных ролей: root, admin, manager
        const userRole = userData.role?.toString().trim().toLowerCase();
        const allowedRoles = ['root', 'admin', 'manager'];
        console.log('userRole:', userRole);
        if (allowedRoles.includes(userRole)) {
          resolve(userData);
        } else {
          reject(new Error('Доступ запрещён: недостаточно прав'));
          ToastUtils.error('Доступ запрещён: недостаточно прав');
        }
      } else {
        reject(new Error('Пользователь не авторизован'));
        ToastUtils.error('Пользователь не авторизован');
      }
    } catch (error) {
      reject(error);
    }
  });
};

// Выход из системы
export const logout = () => {
  return new Promise((resolve, reject) => {
    // Сначала выполняем запрос к серверу выхода
    const token = getTokenFromStorage();
    
    if (token) {
      fetch(`${SSO_API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          console.warn('Не удалось выполнить logout на сервере');
        }
        return response.json();
      })
      .catch(error => {
        console.warn('Ошибка при выполнении logout на сервере:', error);
      })
      .finally(() => {
        // В любом случае удаляем данные из localStorage
        const removed = removeAuthDataFromStorage();
        if (removed) {
          resolve();
        } else {
          reject(new Error('Не удалось удалить данные аутентификации'));
        }
      });
    } else {
      // Если токена нет, просто удаляем данные из localStorage
      const removed = removeAuthDataFromStorage();
      if (removed) {
        resolve();
      } else {
        reject(new Error('Не удалось удалить данные аутентификации'));
      }
    }
  });
};

// Получение заголовков с авторизацией для последующих запросов
// Автоматически обновляет токен при необходимости
export const getAuthHeaders = async () => {
  try {
    let token = getTokenFromStorage();
    
    // Проверяем, существует ли токен и не истек ли он
    if (token && isAccessTokenExpired()) {
      // Пытаемся обновить токен
      token = await refreshAccessToken();
    }
    
    return setAuthHeader(token);
  } catch (error) {
    // При ошибке обновления токена возвращаем пустые заголовки
    // Это приведет к ошибке в последующих запросах, что нормально
    console.error('Не удалось получить заголовки авторизации:', error);
    return {};
  }
};